/**
 * GameViewport — Orchestrates the live preview canvas and slot game runtime.
 *
 * The visual rendering is now handled by LivePreviewCanvas (PixiJS-based).
 * This component preserves the cascade slot engine hook and API mode logic
 * so the runtime store stays driven during play mode.
 */
import React, { useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { LivePreviewCanvas } from './LivePreviewCanvas';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { useSceneStore } from '../../store/sceneStore';
import { useCascadeSlotEngine } from '../../hooks/useCascadeSlotEngine';
import { useSlotGameRuntimeStore } from '../../store/slotGameRuntimeStore';
import { DEFAULT_CASCADE_SLOT_CONFIG } from '@core/types/cascade-slot.types';
import type { CascadeSlotConfig } from '@core/types/cascade-slot.types';
import type { BackendGameComponentData } from '@core/types/backend-game.types';
import { convertBackendToSlotConfig } from '../../services/backend-game/ConfigConverter';

interface GameViewportProps {
  className?: string;
}

export const GameViewport: React.FC<GameViewportProps> = ({ className }) => {
  const playState = useLivePreviewStore((s) => s.playState);
  const gameObjects = useSceneStore((s) => s.gameObjects);
  const isEffectivelyVisible = useSceneStore((s) => s.isEffectivelyVisible);
  const activeMode = useSlotGameRuntimeStore((s) => s.activeMode);

  // --- Cascade slot engine orchestration (drives slotGameRuntimeStore) ---

  // Detect cascade-slot in scene (only if visible)
  const cascadeSlotComponent = useMemo(() => {
    const obj = Object.values(gameObjects).find(
      (go) =>
        isEffectivelyVisible(go.id) &&
        go.components.some((c) => c.type === 'cascade-slot-config')
    );
    return obj?.components.find((c) => c.type === 'cascade-slot-config') ?? null;
  }, [gameObjects, isEffectivelyVisible]);

  // Detect backend-game in scene (only if visible)
  const backendGameData = useMemo(() => {
    const obj = Object.values(gameObjects).find(
      (go) =>
        isEffectivelyVisible(go.id) &&
        go.components.some((c) => c.type === 'backend-game-config')
    );
    const comp = obj?.components.find((c) => c.type === 'backend-game-config');
    return (comp?.data as BackendGameComponentData | undefined) ?? null;
  }, [gameObjects, isEffectivelyVisible]);

  const hasCascadeSlot = !!cascadeSlotComponent || !!backendGameData?.backendConfig;

  // Build cascade config from component data (fall back to defaults)
  const cascadeConfig = useMemo<CascadeSlotConfig | null>(() => {
    // Backend game takes priority
    if (backendGameData?.backendConfig) {
      try {
        return convertBackendToSlotConfig({ ...backendGameData, activeMode });
      } catch (err) {
        console.warn('[GameViewport] Backend config conversion failed:', err);
        return { ...DEFAULT_CASCADE_SLOT_CONFIG };
      }
    }
    if (!cascadeSlotComponent) return null;
    return {
      ...DEFAULT_CASCADE_SLOT_CONFIG,
      ...cascadeSlotComponent.data,
    } as CascadeSlotConfig;
  }, [cascadeSlotComponent, backendGameData, activeMode]);

  // Always enable API mode for backend games when playing
  useEffect(() => {
    if (playState === 'playing' && backendGameData) {
      useSlotGameRuntimeStore.getState().setApiMode(true);
    } else if (playState !== 'playing') {
      useSlotGameRuntimeStore.getState().setApiMode(false);
    }
  }, [playState, !!backendGameData]);

  // Initialize cascade slot engine (hook called unconditionally)
  useCascadeSlotEngine({
    config: cascadeConfig,
    isPlaying: playState === 'playing' && hasCascadeSlot,
  });

  // --- Render ---

  return <LivePreviewCanvas className={className} />;
};
